/**
 * URL scheme allowlist for anything that came from a text field.
 *
 * Day notes are Markdown written by an advisor and read by a traveller. The
 * threat is not really a malicious Ana — it is that this same renderer will one
 * day show text a traveller wrote, or text imported from an email, and the
 * decision about what a link may do should already have been made by then.
 */

export const ALLOWED_URL_SCHEMES = ['http', 'https', 'mailto', 'tel'] as const

const allowed = new Set<string>(ALLOWED_URL_SCHEMES)

/** Tab, line feed and carriage return: removed from anywhere in a URL by the parser. */
const IGNORED_ANYWHERE = new Set([0x09, 0x0a, 0x0d])

/** C0 controls and space: trimmed from both ends by the parser. */
const TRIMMED_AT_EDGES = (code: number): boolean => code <= 0x20

/**
 * Removes the characters a URL parser ignores.
 *
 * This is the whole trick, and it has to happen BEFORE the scheme is read.
 * WHATWG URL parsing strips every tab and newline from anywhere in the input and
 * trims leading and trailing C0 controls and spaces. So a note containing
 * `jav<TAB>ascript:alert(1)` reaches the browser as `javascript:alert(1)`, and
 * code that inspects the raw string while handing the raw string to the browser
 * is checking a different value from the one that runs.
 *
 * Written as an explicit character walk rather than a regular expression
 * containing invisible literals, because a control character inside a character
 * class is impossible to review and trivially lost to a copy-paste.
 */
function normalise(raw: string): string {
  let kept = ''
  for (const character of raw) {
    const code = character.codePointAt(0)!
    if (!IGNORED_ANYWHERE.has(code)) kept += character
  }

  let start = 0
  let end = kept.length
  while (start < end && TRIMMED_AT_EDGES(kept.charCodeAt(start))) start++
  while (end > start && TRIMMED_AT_EDGES(kept.charCodeAt(end - 1))) end--
  return kept.slice(start, end)
}

const SCHEME = /^([a-zA-Z][a-zA-Z0-9+.-]*):/

/**
 * Returns a URL safe to put in an `href`, or null.
 *
 * Returns the *normalised* string rather than the input, so what is rendered is
 * exactly what was inspected. Handing back the original would reintroduce the
 * bypass this function exists to close.
 *
 * Schemeless input — `//evil.example`, `foo.html`, `#anchor` — is rejected
 * rather than resolved. This renderer has no business producing relative links,
 * and a protocol-relative URL is a scheme decision made by somebody else.
 */
export function safeUrl(raw: string | null | undefined): string | null {
  if (!raw) return null

  const normalised = normalise(raw)
  if (normalised === '') return null

  const match = SCHEME.exec(normalised)
  if (!match) return null

  return allowed.has(match[1]!.toLowerCase()) ? normalised : null
}
